import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage, isNetworkOrServerFailure } from "@/shared/lib/errors.js";
import { createInforme } from "../services/reports.service.js";
import { TIPO_LABEL } from "../types.js";
import type { TipoInforme } from "../types.js";
import { ClienteCombobox, type ClienteValue } from "./ClienteCombobox.js";
import { putReport, enqueueReport } from "@/features/offline-sync/index.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const TIPOS: TipoInforme[] = ["IE", "IS", "AC"];

const TIPO_DESC: Record<TipoInforme, string> = {
  IE: "Evaluación de estado de equipos.",
  IS: "Reporte de servicio en campo.",
  AC: "Acta de conformidad del cliente.",
};

function isTipo(value: string | null): value is TipoInforme {
  return value === "IE" || value === "IS" || value === "AC";
}

/** Página /informes/nuevo — elegí tipo + cliente y creá el informe. */
export function NuevoInforme() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tipoParam = searchParams.get("tipo");

  const [tipo, setTipo] = useState<TipoInforme | null>(isTipo(tipoParam) ? tipoParam : null);
  const [cliente, setCliente] = useState<ClienteValue>({ nombre: "" });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clienteListo = cliente.clienteId !== undefined || cliente.nombre.trim().length > 0;
  const puedeEnviar = tipo !== null && clienteListo && !enviando;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (tipo === null || !clienteListo) return;
    setEnviando(true);
    setError(null);

    // S1: mint a localId for the new report regardless of network state.
    // If the network POST succeeds we update the local record with serverId.
    // If offline, the local record persists and will sync on reconnect (S2).
    const localId = crypto.randomUUID();
    const clienteNombre = cliente.nombre.trim();
    const now = new Date().toISOString();

    try {
      // Write locally first (optimistic, NFR-2 non-blocking).
      // This putReport is inside the try so an IDB quota/blocked error is
      // caught here instead of leaving setEnviando stuck at true.
      await putReport({
        localId,
        tipo,
        clienteId: cliente.clienteId,
        cliente: clienteNombre || undefined,
        estado: "borrador",
        payload: {},
        updatedAt: now,
      });

      const nuevo = await createInforme(
        cliente.clienteId !== undefined
          ? { tipo, clienteId: cliente.clienteId }
          : { tipo, cliente: clienteNombre },
      );
      // Update local record with serverId so it reconciles on next read.
      await putReport({
        localId,
        serverId: nuevo.id,
        numero: nuevo.numero,
        tipo,
        clienteId: nuevo.clienteId ?? undefined,
        cliente: nuevo.cliente,
        estado: nuevo.estado,
        payload: nuevo.payload,
        updatedAt: nuevo.updatedAt,
      });
      toast.success("Informe creado");
      // Navigate to server id so the editor can load from API while online.
      navigate(`/informe/${nuevo.id}`);
    } catch (err) {
      // Fix 5: enqueue on ANY network/server failure, not only when offline.
      // If createInforme fails with a 5xx error while navigator.onLine === true,
      // the locally-saved borrador would become an orphan with no outbox entry.
      // isNetworkOrServerFailure returns false for 4xx (validation errors) so those
      // still surface to the user rather than being silently enqueued.
      if (!navigator.onLine || isNetworkOrServerFailure(err)) {
        await enqueueReport({
          localId,
          tipo,
          clienteId: cliente.clienteId,
          cliente: clienteNombre || undefined,
          payload: {},
          updatedAt: now,
        });
        const msg = !navigator.onLine
          ? "Sin señal — informe guardado localmente"
          : "Error del servidor — informe guardado para sincronizar";
        toast.success(msg);
        navigate(`/informe/${localId}`);
      } else {
        const msg = friendlyMessage(err);
        setError(msg);
        toast.error(msg);
        setEnviando(false);
      }
    }
  }

  return (
    <div className="paper-surface min-h-screen">
      <main className="mx-auto max-w-2xl px-6 py-10">
        {/* Volver (sin barra de cabecera, como el mock) */}
        <div className="mb-8 flex justify-end">
          {/* navigate(-1) returns to wherever we came from (admin or técnico). */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Volver
          </button>
        </div>

        <div className="forja-rise space-y-2" style={{ animationDelay: "60ms" }}>
          <p className={labelClass}>Nuevo informe</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Crear un <span className="italic font-medium">informe</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Elige el tipo y el cliente. El número se genera automáticamente al crear.
          </p>
        </div>

        <form
          id="nuevo-informe-form"
          onSubmit={handleSubmit}
          className="forja-rise mt-8 space-y-6 rounded-3xl border border-border bg-card p-6 md:p-8"
          style={{ animationDelay: "120ms" }}
        >
          {/* ── Tipo de informe (filas seleccionables) ───────────────────── */}
          <fieldset className="space-y-3">
            <legend className={labelClass}>Tipo de informe</legend>
            <div className="space-y-3">
              {TIPOS.map((t) => {
                const active = tipo === t;
                return (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTipo(t)}
                    className={cn(
                      "flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-all",
                      active
                        ? "border-brand-accent bg-brand-accent/5"
                        : "border-border bg-card hover:border-brand-accent/40",
                    )}
                  >
                    <TipoBadge tipo={t} />
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-sm font-semibold text-foreground">
                        {TIPO_LABEL[t]}
                      </p>
                      <p className="text-xs text-muted-foreground">{TIPO_DESC[t]}</p>
                    </div>
                    {active && (
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand-accent text-brand-accent-foreground">
                        <Check className="size-4" aria-hidden />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="border-t border-border" />

          {/* ── Cliente ──────────────────────────────────────────────────── */}
          <div className="space-y-2">
            <ClienteCombobox value={cliente} onChange={setCliente} disabled={enviando} />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </form>

        {/* ── Acción flotante (debajo de la tarjeta, como el mock) ───────── */}
        <div className="mt-6 flex justify-end">
          <Button
            type="submit"
            form="nuevo-informe-form"
            disabled={!puedeEnviar}
            size="lg"
            className="gap-2 rounded-full bg-brand-accent px-6 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-brand-accent-foreground hover:bg-brand-accent/90"
          >
            {enviando && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {enviando ? "Creando…" : "Crear informe"}
          </Button>
        </div>
      </main>
    </div>
  );
}
